// src/utils/purchaseOrderExport.js
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { formatDate, formatDateTime } from './dateUtils';
import { formatCurrency } from './orderCalculations';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  draft: 'Draft',
  pending: 'Pending',
  received: 'Received',
  partial: 'Partially Received',
  cancelled: 'Cancelled'
};

const STATUS_BADGE_COLORS = {
  draft: { bg: [243, 244, 246], text: [107, 114, 128] },
  pending: { bg: [254, 243, 199], text: [180, 83, 9] },
  received: { bg: [209, 250, 229], text: [6, 95, 70] },
  partial: { bg: [219, 234, 254], text: [29, 78, 216] },
  cancelled: { bg: [254, 226, 226], text: [185, 28, 28] }
};

const PRIMARY = [79, 70, 229];
const PRIMARY_DARK = [55, 48, 163];
const DARK = [17, 24, 39];
const MEDIUM = [75, 85, 99];
const MUTED = [156, 163, 175];
const BORDER = [229, 231, 235];
const BG_LIGHT = [249, 250, 251];
const WHITE = [255, 255, 255];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getStatusLabel = (status) => STATUS_LABELS[status] || status;

const getItemVariationLabel = (item) => {
  if (!item.variationId || !item.ProductVariation) return null;
  const attrs = item.ProductVariation.attributeValues;
  if (attrs && attrs.length > 0) {
    return attrs.map(a => `${a.ProductAttribute?.name}: ${a.value}`).join(', ');
  }
  return item.ProductVariation.sku || null;
};

const safeNum = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
};

// ─── PDF Export ───────────────────────────────────────────────────────────────

export const exportPurchaseOrderPDF = (purchaseOrder) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const pageW = doc.internal.pageSize.getWidth();   // 210
  const pageH = doc.internal.pageSize.getHeight();  // 297
  const ml = 14; // margin left
  const mr = 14; // margin right
  const cw = pageW - ml - mr; // content width

  const setColor = (rgb) => doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  const setFill = (rgb) => doc.setFillColor(rgb[0], rgb[1], rgb[2]);
  const setDraw = (rgb) => doc.setDrawColor(rgb[0], rgb[1], rgb[2]);

  // ── Header Banner ─────────────────────────────────────────────────────────
  setFill(PRIMARY);
  doc.rect(0, 0, pageW, 42, 'F');

  // Decorative accent line
  setFill(PRIMARY_DARK);
  doc.rect(0, 38, pageW, 4, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('PURCHASE ORDER', ml, 16);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(purchaseOrder.purchaseOrderNumber, ml, 26);

  // Status badge top-right
  const statusLabel = getStatusLabel(purchaseOrder.status);
  const badgeColors = STATUS_BADGE_COLORS[purchaseOrder.status] || STATUS_BADGE_COLORS.draft;
  const badgeText = statusLabel.toUpperCase();
  const badgeW = 46;
  const badgeX = pageW - mr - badgeW;
  const badgeY = 13;

  setFill(WHITE);
  doc.roundedRect(badgeX, badgeY, badgeW, 10, 2, 2, 'F');
  doc.setTextColor(badgeColors.text[0], badgeColors.text[1], badgeColors.text[2]);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.text(badgeText, badgeX + badgeW / 2, badgeY + 6.5, { align: 'center' });

  // Generated date top-right below badge
  doc.setTextColor(200, 200, 220);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${formatDate(new Date().toISOString())}`, pageW - mr, 32, { align: 'right' });

  let y = 50;

  // ── Order Info Strip ──────────────────────────────────────────────────────
  const infoColW = cw / 3;

  setFill(BG_LIGHT);
  setDraw(BORDER);
  doc.rect(ml, y, cw, 22, 'FD');

  // Dividers between columns
  doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
  doc.line(ml + infoColW, y, ml + infoColW, y + 22);
  doc.line(ml + infoColW * 2, y, ml + infoColW * 2, y + 22);

  const drawInfoCol = (label, value, x) => {
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    setColor(MUTED);
    doc.text(label.toUpperCase(), x + 5, y + 7);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    setColor(DARK);
    doc.text(value || '—', x + 5, y + 16);
  };

  drawInfoCol('Order Date', formatDate(purchaseOrder.orderDate), ml);
  drawInfoCol(
    'Expected Delivery',
    purchaseOrder.expectedDeliveryDate ? formatDate(purchaseOrder.expectedDeliveryDate) : 'Not Specified',
    ml + infoColW
  );
  drawInfoCol('Created By', purchaseOrder.createdBy?.name || '—', ml + infoColW * 2);

  y += 28;

  // ── Supplier & Warehouse ──────────────────────────────────────────────────
  const colGap = 6;
  const halfW = (cw - colGap) / 2;

  const drawCard = (title, lines, x, startY, w) => {
    // Card header
    setFill(PRIMARY);
    doc.rect(x, startY, w, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.text(title, x + 5, startY + 5.5);

    // Card body
    const lineH = 5.5;
    const bodyH = Math.max(lines.length * lineH + 10, 24);
    setFill(WHITE);
    setDraw(BORDER);
    doc.rect(x, startY + 8, w, bodyH, 'FD');

    lines.forEach((line, i) => {
      if (line.bold) {
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'bold');
        setColor(DARK);
      } else {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        setColor(MEDIUM);
      }
      const lineY = startY + 8 + 7 + i * lineH;
      // Truncate long text to fit card width
      const maxChars = Math.floor((w - 10) / 1.8);
      const text = (line.text || '').length > maxChars
        ? (line.text || '').substring(0, maxChars - 2) + '…'
        : (line.text || '');
      doc.text(text, x + 5, lineY);
    });

    return startY + 8 + bodyH + 4;
  };

  const supplier = purchaseOrder.Supplier;
  const supplierLines = supplier ? [
    { text: supplier.name, bold: true },
    ...(supplier.contactPerson ? [{ text: `Contact: ${supplier.contactPerson}` }] : []),
    ...(supplier.email ? [{ text: `Email: ${supplier.email}` }] : []),
    ...(supplier.phone ? [{ text: `Phone: ${supplier.phone}` }] : []),
    ...(supplier.address ? [{ text: supplier.address }] : []),
    ...((supplier.city || supplier.country)
      ? [{ text: [supplier.city, supplier.country].filter(Boolean).join(', ') }]
      : [])
  ] : [{ text: 'Not specified' }];

  const warehouse = purchaseOrder.Warehouse;
  const warehouseLines = warehouse ? [
    { text: warehouse.name, bold: true },
    ...(warehouse.address ? [{ text: warehouse.address }] : []),
    ...(warehouse.phone ? [{ text: `Phone: ${warehouse.phone}` }] : [])
  ] : [{ text: 'Not specified' }];

  const leftEnd = drawCard('SUPPLIER INFORMATION', supplierLines, ml, y, halfW);
  const rightEnd = drawCard('WAREHOUSE INFORMATION', warehouseLines, ml + halfW + colGap, y, halfW);
  y = Math.max(leftEnd, rightEnd);

  // Notes (if any)
  if (purchaseOrder.notes) {
    setFill(BG_LIGHT);
    setDraw(BORDER);
    doc.rect(ml, y, cw, 14, 'FD');
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    setColor(MUTED);
    doc.text('NOTES', ml + 5, y + 5.5);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    setColor(MEDIUM);
    const notesMaxChars = Math.floor(cw / 1.6);
    const notesText = purchaseOrder.notes.length > notesMaxChars
      ? purchaseOrder.notes.substring(0, notesMaxChars - 2) + '…'
      : purchaseOrder.notes;
    doc.text(notesText, ml + 5, y + 11);
    y += 18;
  }

  y += 2;

  // ── Items Table ───────────────────────────────────────────────────────────
  const items = purchaseOrder.items || [];

  const tableBody = items.map((item, idx) => {
    const sku = item.ProductVariation?.sku || item.Product?.sku || '';
    const variation = getItemVariationLabel(item);
    const parts = [
      item.Product?.name || 'N/A',
      sku ? `SKU: ${sku}` : '',
      variation || '',
      item.notes ? `Note: ${item.notes}` : ''
    ].filter(Boolean);

    return [
      String(idx + 1),
      parts.join('\n'),
      String(safeNum(item.quantity)),
      String(safeNum(item.receivedQuantity)),
      formatCurrency(item.unitPrice),
      formatCurrency(item.lineTotal)
    ];
  });

  autoTable(doc, {
    startY: y,
    margin: { left: ml, right: mr },
    head: [['#', 'Product / Description', 'Ordered', 'Received', 'Unit Price', 'Line Total']],
    body: tableBody,
    theme: 'plain',
    styles: {
      fontSize: 8,
      cellPadding: { top: 3.5, bottom: 3.5, left: 4, right: 4 },
      textColor: [DARK[0], DARK[1], DARK[2]],
      lineColor: [BORDER[0], BORDER[1], BORDER[2]],
      lineWidth: 0.25,
      valign: 'top'
    },
    headStyles: {
      fillColor: [PRIMARY[0], PRIMARY[1], PRIMARY[2]],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7.5,
      cellPadding: { top: 4, bottom: 4, left: 4, right: 4 }
    },
    alternateRowStyles: {
      fillColor: [BG_LIGHT[0], BG_LIGHT[1], BG_LIGHT[2]]
    },
    columnStyles: {
      0: { cellWidth: 9, halign: 'center', valign: 'middle' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 22, halign: 'center', valign: 'middle' },
      3: { cellWidth: 22, halign: 'center', valign: 'middle' },
      4: { cellWidth: 26, halign: 'right', valign: 'middle' },
      5: { cellWidth: 28, halign: 'right', valign: 'middle', fontStyle: 'bold' }
    },
    didDrawPage: (data) => {
      // Page footer
      doc.setFontSize(7);
      setColor(MUTED);
      doc.text(
        `Purchase Order — ${purchaseOrder.purchaseOrderNumber}`,
        ml,
        pageH - 7
      );
      doc.text(
        `Page ${data.pageNumber}`,
        pageW - mr,
        pageH - 7,
        { align: 'right' }
      );
      setDraw(BORDER);
      doc.line(ml, pageH - 10, pageW - mr, pageH - 10);
    }
  });

  y = doc.lastAutoTable.finalY + 6;

  // ── Financial Summary ─────────────────────────────────────────────────────
  const sumW = 76;
  const sumX = pageW - mr - sumW;

  const financialRows = [
    { label: 'Subtotal', value: formatCurrency(purchaseOrder.totalAmount), bold: false }
  ];

  if (safeNum(purchaseOrder.extraCharges) > 0) {
    const lbl = purchaseOrder.extraChargesDescription
      ? `Extra Charges (${purchaseOrder.extraChargesDescription})`
      : 'Extra Charges';
    financialRows.push({ label: lbl, value: `+ ${formatCurrency(purchaseOrder.extraCharges)}`, bold: false });
  }

  if (safeNum(purchaseOrder.discountAmount) > 0) {
    const lbl = purchaseOrder.discountDescription
      ? `Discount (${purchaseOrder.discountDescription})`
      : 'Discount';
    financialRows.push({ label: lbl, value: `- ${formatCurrency(purchaseOrder.discountAmount)}`, bold: false });
  }

  const totalRows = financialRows.length;
  const rowH = 8;
  const totalH = totalRows * rowH + 11;

  if (y + totalH > pageH - 20) {
    doc.addPage();
    y = 20;
  }

  let fy = y;

  financialRows.forEach(({ label, value, bold }) => {
    setFill(BG_LIGHT);
    setDraw(BORDER);
    doc.rect(sumX, fy, sumW, rowH, 'FD');
    doc.setFontSize(bold ? 9 : 8);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    setColor(MEDIUM);
    doc.text(label, sumX + 4, fy + 5.5);
    setColor(bold ? DARK : MEDIUM);
    doc.text(value, sumX + sumW - 4, fy + 5.5, { align: 'right' });
    fy += rowH;
  });

  // Grand total row
  setFill(PRIMARY);
  doc.rect(sumX, fy, sumW, 11, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('Grand Total', sumX + 4, fy + 7.5);
  doc.text(formatCurrency(purchaseOrder.grandTotal), sumX + sumW - 4, fy + 7.5, { align: 'right' });

  // Audit info below financial (if received/cancelled)
  if (purchaseOrder.receivedBy || purchaseOrder.cancelledBy) {
    const auditY = fy + 18;
    if (auditY < pageH - 20) {
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      setColor(MUTED);
      if (purchaseOrder.receivedBy) {
        doc.text(
          `Received by ${purchaseOrder.receivedBy.name}${purchaseOrder.receivedDate ? ` on ${formatDate(purchaseOrder.receivedDate)}` : ''}`,
          ml,
          auditY
        );
      }
      if (purchaseOrder.cancelledBy) {
        doc.text(
          `Cancelled by ${purchaseOrder.cancelledBy.name}${purchaseOrder.cancelledAt ? ` on ${formatDateTime(purchaseOrder.cancelledAt)}` : ''}`,
          ml,
          purchaseOrder.receivedBy ? auditY + 5 : auditY
        );
      }
    }
  }

  doc.save(`purchase-order-${purchaseOrder.purchaseOrderNumber}.pdf`);
};

// ─── Excel Export ─────────────────────────────────────────────────────────────

export const exportPurchaseOrderExcel = async (purchaseOrder) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Rettzy';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Purchase Order', {
    pageSetup: {
      paperSize: 9,
      orientation: 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      margins: { left: 0.5, right: 0.5, top: 0.75, bottom: 0.75 }
    },
    views: [{ showGridLines: false }]
  });

  sheet.columns = [
    { key: 'c1', width: 6 },
    { key: 'c2', width: 32 },
    { key: 'c3', width: 16 },
    { key: 'c4', width: 16 },
    { key: 'c5', width: 16 },
    { key: 'c6', width: 18 }
  ];

  // Style presets
  const fills = {
    primary: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F46E5' } },
    primaryDark: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF3730A3' } },
    light: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } },
    altRow: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F1FF' } },
    white: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } },
    totalBg: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDE9FE' } }
  };

  const borders = {
    thin: {
      top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      right: { style: 'thin', color: { argb: 'FFE5E7EB' } }
    },
    thickBottom: {
      top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      bottom: { style: 'medium', color: { argb: 'FF4F46E5' } },
      right: { style: 'thin', color: { argb: 'FFE5E7EB' } }
    }
  };

  let rn = 1;
  const getRow = (n) => sheet.getRow(n);
  const mergeAndSet = (r1, c1, r2, c2) => sheet.mergeCells(r1, c1, r2, c2);

  // ── Title ──────────────────────────────────────────────────────────────────
  const titleRow = getRow(rn);
  titleRow.height = 40;
  mergeAndSet(rn, 1, rn, 6);
  const titleCell = titleRow.getCell(1);
  titleCell.value = 'PURCHASE ORDER';
  titleCell.font = { bold: true, size: 20, color: { argb: 'FFFFFFFF' }, name: 'Calibri' };
  titleCell.fill = fills.primary;
  titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  rn++;

  // PO number + status row
  const poRow = getRow(rn);
  poRow.height = 24;
  mergeAndSet(rn, 1, rn, 4);
  const poCell = poRow.getCell(1);
  poCell.value = purchaseOrder.purchaseOrderNumber;
  poCell.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' }, name: 'Calibri' };
  poCell.fill = fills.primaryDark;
  poCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };

  mergeAndSet(rn, 5, rn, 6);
  const statusCell = poRow.getCell(5);
  statusCell.value = getStatusLabel(purchaseOrder.status).toUpperCase();
  statusCell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' }, name: 'Calibri' };
  statusCell.fill = fills.primaryDark;
  statusCell.alignment = { vertical: 'middle', horizontal: 'right', indent: 1 };
  rn++;

  // Spacer
  getRow(rn).height = 8;
  rn++;

  // ── Order Details ──────────────────────────────────────────────────────────
  const addInfoRow = (label, value, label2, value2) => {
    const row = getRow(rn);
    row.height = 18;

    const lc = row.getCell(1);
    lc.value = label;
    lc.font = { bold: true, size: 8.5, color: { argb: 'FF6B7280' }, name: 'Calibri' };
    lc.fill = fills.light;
    lc.border = borders.thin;
    lc.alignment = { vertical: 'middle', indent: 1 };

    mergeAndSet(rn, 2, rn, label2 ? 3 : 6);
    const vc = row.getCell(2);
    vc.value = value;
    vc.font = { size: 9, color: { argb: 'FF111827' }, name: 'Calibri' };
    vc.fill = fills.white;
    vc.border = borders.thin;
    vc.alignment = { vertical: 'middle', indent: 1 };

    if (label2) {
      const lc2 = row.getCell(4);
      lc2.value = label2;
      lc2.font = { bold: true, size: 8.5, color: { argb: 'FF6B7280' }, name: 'Calibri' };
      lc2.fill = fills.light;
      lc2.border = borders.thin;
      lc2.alignment = { vertical: 'middle', indent: 1 };

      mergeAndSet(rn, 5, rn, 6);
      const vc2 = row.getCell(5);
      vc2.value = value2;
      vc2.font = { size: 9, color: { argb: 'FF111827' }, name: 'Calibri' };
      vc2.fill = fills.white;
      vc2.border = borders.thin;
      vc2.alignment = { vertical: 'middle', indent: 1 };
    }

    rn++;
  };

  addInfoRow('Order Date', formatDate(purchaseOrder.orderDate), 'Status', getStatusLabel(purchaseOrder.status));
  addInfoRow(
    'Expected Delivery',
    purchaseOrder.expectedDeliveryDate ? formatDate(purchaseOrder.expectedDeliveryDate) : 'Not Specified',
    'Created By',
    purchaseOrder.createdBy?.name || '—'
  );

  if (purchaseOrder.notes) {
    const notesRow = getRow(rn);
    notesRow.height = 18;

    const nlc = notesRow.getCell(1);
    nlc.value = 'Notes';
    nlc.font = { bold: true, size: 8.5, color: { argb: 'FF6B7280' }, name: 'Calibri' };
    nlc.fill = fills.light;
    nlc.border = borders.thin;
    nlc.alignment = { vertical: 'middle', indent: 1 };

    mergeAndSet(rn, 2, rn, 6);
    const nvc = notesRow.getCell(2);
    nvc.value = purchaseOrder.notes;
    nvc.font = { size: 9, color: { argb: 'FF374151' }, name: 'Calibri' };
    nvc.fill = fills.white;
    nvc.border = borders.thin;
    nvc.alignment = { vertical: 'middle', indent: 1, wrapText: true };
    rn++;
  }

  // Spacer
  getRow(rn).height = 8;
  rn++;

  // ── Supplier & Warehouse ───────────────────────────────────────────────────
  // Headers
  const swHeaderRow = getRow(rn);
  swHeaderRow.height = 18;
  mergeAndSet(rn, 1, rn, 3);
  const supHeader = swHeaderRow.getCell(1);
  supHeader.value = 'SUPPLIER INFORMATION';
  supHeader.font = { bold: true, size: 8.5, color: { argb: 'FFFFFFFF' }, name: 'Calibri' };
  supHeader.fill = fills.primary;
  supHeader.alignment = { vertical: 'middle', horizontal: 'center' };

  mergeAndSet(rn, 4, rn, 6);
  const whHeader = swHeaderRow.getCell(4);
  whHeader.value = 'WAREHOUSE INFORMATION';
  whHeader.font = { bold: true, size: 8.5, color: { argb: 'FFFFFFFF' }, name: 'Calibri' };
  whHeader.fill = fills.primary;
  whHeader.alignment = { vertical: 'middle', horizontal: 'center' };
  rn++;

  const supplier = purchaseOrder.Supplier;
  const warehouse = purchaseOrder.Warehouse;

  const supLines = supplier ? [
    { text: supplier.name, bold: true },
    ...(supplier.contactPerson ? [{ text: `Contact: ${supplier.contactPerson}` }] : []),
    ...(supplier.email ? [{ text: `Email: ${supplier.email}` }] : []),
    ...(supplier.phone ? [{ text: `Phone: ${supplier.phone}` }] : []),
    ...(supplier.address ? [{ text: supplier.address }] : []),
    ...((supplier.city || supplier.country)
      ? [{ text: [supplier.city, supplier.country].filter(Boolean).join(', ') }]
      : [])
  ] : [{ text: 'Not specified' }];

  const whLines = warehouse ? [
    { text: warehouse.name, bold: true },
    ...(warehouse.address ? [{ text: warehouse.address }] : []),
    ...(warehouse.phone ? [{ text: `Phone: ${warehouse.phone}` }] : [])
  ] : [{ text: 'Not specified' }];

  const maxContactLines = Math.max(supLines.length, whLines.length);

  for (let i = 0; i < maxContactLines; i++) {
    const crow = getRow(rn);
    crow.height = 16;

    mergeAndSet(rn, 1, rn, 3);
    const sc = crow.getCell(1);
    sc.value = supLines[i]?.text || '';
    sc.font = {
      bold: !!supLines[i]?.bold,
      size: supLines[i]?.bold ? 10 : 8.5,
      color: { argb: supLines[i]?.bold ? 'FF111827' : 'FF4B5563' },
      name: 'Calibri'
    };
    sc.fill = fills.white;
    sc.border = borders.thin;
    sc.alignment = { vertical: 'middle', indent: 1 };

    mergeAndSet(rn, 4, rn, 6);
    const wc = crow.getCell(4);
    wc.value = whLines[i]?.text || '';
    wc.font = {
      bold: !!whLines[i]?.bold,
      size: whLines[i]?.bold ? 10 : 8.5,
      color: { argb: whLines[i]?.bold ? 'FF111827' : 'FF4B5563' },
      name: 'Calibri'
    };
    wc.fill = fills.white;
    wc.border = borders.thin;
    wc.alignment = { vertical: 'middle', indent: 1 };

    rn++;
  }

  // Spacer
  getRow(rn).height = 10;
  rn++;

  // ── Items Table ────────────────────────────────────────────────────────────
  const itemHeaderRow = getRow(rn);
  itemHeaderRow.height = 22;
  const itemCols = ['#', 'Product / Description', 'Ordered Qty', 'Received Qty', 'Unit Price', 'Line Total'];
  const itemAligns = ['center', 'left', 'right', 'right', 'right', 'right'];

  itemCols.forEach((h, i) => {
    const cell = itemHeaderRow.getCell(i + 1);
    cell.value = h;
    cell.font = { bold: true, size: 9, color: { argb: 'FFFFFFFF' }, name: 'Calibri' };
    cell.fill = fills.primary;
    cell.border = borders.thin;
    cell.alignment = { vertical: 'middle', horizontal: itemAligns[i], indent: i === 1 ? 1 : 0 };
  });
  rn++;

  const items = purchaseOrder.items || [];
  items.forEach((item, idx) => {
    const sku = item.ProductVariation?.sku || item.Product?.sku;
    const variation = getItemVariationLabel(item);
    const productParts = [
      item.Product?.name || 'N/A',
      sku ? `SKU: ${sku}` : null,
      variation || null,
      item.notes ? `Note: ${item.notes}` : null
    ].filter(Boolean).join('\n');

    const isAlt = idx % 2 === 1;
    const irow = getRow(rn);
    irow.height = productParts.includes('\n') ? 30 : 18;

    const rowValues = [
      idx + 1,
      productParts,
      safeNum(item.quantity),
      safeNum(item.receivedQuantity),
      safeNum(item.unitPrice),
      safeNum(item.lineTotal)
    ];

    rowValues.forEach((val, i) => {
      const cell = irow.getCell(i + 1);
      cell.value = val;
      cell.font = {
        bold: i === 5,
        size: 9,
        color: { argb: i === 5 ? 'FF111827' : 'FF374151' },
        name: 'Calibri'
      };
      cell.fill = isAlt ? fills.altRow : fills.white;
      cell.border = borders.thin;
      cell.alignment = {
        vertical: 'middle',
        horizontal: itemAligns[i],
        indent: i === 1 ? 1 : 0,
        wrapText: i === 1
      };
      if (i >= 4) {
        cell.numFmt = '#,##0.00';
      }
    });

    rn++;
  });

  // Spacer
  getRow(rn).height = 8;
  rn++;

  // ── Financial Summary ──────────────────────────────────────────────────────
  const addFinRow = (label, value, isTotal) => {
    const row = getRow(rn);
    row.height = isTotal ? 22 : 17;

    mergeAndSet(rn, 1, rn, 4);
    const spacer = row.getCell(1);
    spacer.fill = fills.white;

    mergeAndSet(rn, 5, rn, 5);
    const lc = row.getCell(5);
    lc.value = label;
    lc.font = {
      bold: isTotal,
      size: isTotal ? 10 : 8.5,
      color: { argb: isTotal ? 'FFFFFFFF' : 'FF4B5563' },
      name: 'Calibri'
    };
    lc.fill = isTotal ? fills.primary : fills.light;
    lc.border = isTotal ? undefined : borders.thin;
    lc.alignment = { vertical: 'middle', indent: 1 };

    const vc = row.getCell(6);
    vc.value = value;
    vc.font = {
      bold: isTotal,
      size: isTotal ? 10 : 8.5,
      color: { argb: isTotal ? 'FFFFFFFF' : 'FF111827' },
      name: 'Calibri'
    };
    vc.fill = isTotal ? fills.primary : fills.white;
    vc.border = isTotal ? undefined : borders.thin;
    vc.numFmt = '#,##0.00';
    vc.alignment = { vertical: 'middle', horizontal: 'right' };

    rn++;
  };

  addFinRow('Subtotal', safeNum(purchaseOrder.totalAmount), false);

  if (safeNum(purchaseOrder.extraCharges) > 0) {
    const lbl = purchaseOrder.extraChargesDescription
      ? `Extra Charges (${purchaseOrder.extraChargesDescription})`
      : 'Extra Charges';
    addFinRow(lbl, safeNum(purchaseOrder.extraCharges), false);
  }

  if (safeNum(purchaseOrder.discountAmount) > 0) {
    const lbl = purchaseOrder.discountDescription
      ? `Discount (${purchaseOrder.discountDescription})`
      : 'Discount';
    addFinRow(lbl, -safeNum(purchaseOrder.discountAmount), false);
  }

  addFinRow('GRAND TOTAL', safeNum(purchaseOrder.grandTotal), true);

  // Spacer
  getRow(rn).height = 8;
  rn++;

  // Audit info
  const addAuditRow = (label, value) => {
    const row = getRow(rn);
    row.height = 14;
    mergeAndSet(rn, 1, rn, 2);
    const lc = row.getCell(1);
    lc.value = label;
    lc.font = { bold: true, size: 7.5, color: { argb: 'FF9CA3AF' }, name: 'Calibri' };
    lc.alignment = { vertical: 'middle' };

    mergeAndSet(rn, 3, rn, 6);
    const vc = row.getCell(3);
    vc.value = value;
    vc.font = { size: 7.5, color: { argb: 'FF6B7280' }, name: 'Calibri' };
    vc.alignment = { vertical: 'middle' };
    rn++;
  };

  if (purchaseOrder.createdBy) {
    addAuditRow(
      'Created By:',
      `${purchaseOrder.createdBy.name} — ${formatDateTime(purchaseOrder.createdAt)}`
    );
  }
  if (purchaseOrder.receivedBy) {
    addAuditRow(
      'Received By:',
      `${purchaseOrder.receivedBy.name}${purchaseOrder.receivedDate ? ` — ${formatDate(purchaseOrder.receivedDate)}` : ''}`
    );
  }
  if (purchaseOrder.cancelledBy) {
    addAuditRow(
      'Cancelled By:',
      `${purchaseOrder.cancelledBy.name}${purchaseOrder.cancelledAt ? ` — ${formatDateTime(purchaseOrder.cancelledAt)}` : ''}`
    );
  }

  // Footer row
  getRow(rn).height = 6;
  rn++;
  const footerRow = getRow(rn);
  mergeAndSet(rn, 1, rn, 6);
  const footerCell = footerRow.getCell(1);
  footerCell.value = `Generated on ${new Date().toLocaleString()}`;
  footerCell.font = { size: 7, italic: true, color: { argb: 'FF9CA3AF' }, name: 'Calibri' };
  footerCell.alignment = { horizontal: 'right' };

  // Generate and trigger download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `purchase-order-${purchaseOrder.purchaseOrderNumber}.xlsx`;
  anchor.click();
  URL.revokeObjectURL(url);
};
